import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../../i18n/hooks";
import utils from "../../utils";
import IconGooglePlay from "@/assets/common/googlePlay.svg";
import "./PwaInstallingMask.scss";
import { ShareAppBarCloseIcon } from "../Icon/IconSvgMap";

const FALLBACK_MS = 15000;

function IconVerified() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 1.5l3.09 3.09 4.36-.64-.64 4.36L21.5 12l-2.69 3.69.64 4.36-4.36-.64L12 22.5l-3.09-3.09-4.36.64.64-4.36L2.5 12l2.69-3.69-.64-4.36 4.36.64L12 1.5zm-1.2 13.2l5.4-5.4-1.4-1.4-4 4-1.6-1.6-1.4 1.4 3 3z" />
    </svg>
  );
}

export const PwaInstallingMask: React.FC = () => {
  const { t } = useI18n();
  const [showMask, setShowMask] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [progress, setProgress] = useState(0);
  const installedRef = useRef(false);
  const progressRef = useRef(0);
  const rounded = Math.round(progress);
  const isInstalled = useMemo(() => {
    return rounded >= 100;
  }, [progress]);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  // 监听 PWA_INSTALL_ACCEPTED：用户确认安装且未安装过 → 显示遮罩
  // 监听 PWA_INSTALL_REJECTED：用户拒绝 → 不做任何事
  useEffect(() => {
    const onAccepted = () => {
      if (utils.checkPwaEverInstalled()) return;
      installedRef.current = false;
      setProgress(0);
      progressRef.current = 0;
      setShowMask(true);
      setShouldRender(true);
    };

    window.addEventListener("PWA_INSTALL_ACCEPTED", onAccepted);
    return () => {
      window.removeEventListener("PWA_INSTALL_ACCEPTED", onAccepted);
    };
  }, []);

  // 监听 PWA_APP_INSTALLED → 标记安装完成
  useEffect(() => {
    if (!showMask) return;
    const onInstalled = () => {
      installedRef.current = true;
      utils.setPwaEverInstalled();
    };
    window.addEventListener("PWA_APP_INSTALLED", onInstalled);
    return () => window.removeEventListener("PWA_APP_INSTALLED", onInstalled);
  }, [showMask]);

  // 兜底：超时后也标记完成
  useEffect(() => {
    if (!showMask) return;
    const timer = setTimeout(() => {
      installedRef.current = true;
      utils.setPwaEverInstalled();
    }, FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [showMask]);

  // 模拟进度：每 500ms 递增，检测到安装完成则快速推到 100
  useEffect(() => {
    if (!showMask) return;
    const interval = setInterval(() => {
      const cur = progressRef.current;
      if (cur >= 100) {
        clearInterval(interval);
        return;
      }

      if (installedRef.current) {
        setProgress(prev => Math.min(prev + 8 + Math.random() * 4, 100));
        return;
      }

      let increment: number;
      if (cur < 30) increment = 4 + Math.random() * 3;
      else if (cur < 60) increment = 3 + Math.random() * 2;
      else if (cur < 80) increment = 1.5 + Math.random() * 1.5;
      else increment = 0.3 + Math.random() * 0.5;

      setProgress(prev => Math.min(prev + increment, 95));
    }, 500);
    return () => clearInterval(interval);
  }, [showMask]);

  // 淡出动画
  useEffect(() => {
    if (!showMask && shouldRender) {
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [showMask, shouldRender]);

  const handleOkClick = useCallback(() => {
    setShowMask(false);
    utils.openPwaApp();
  }, []);

  const handleCloseClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (isInstalled) {
        setShowMask(false);
        return;
      }
    },
    [isInstalled]
  );

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`pwa-installing-mask${showMask ? "" : " pwa-installing-mask--hidden"}`}
      onClick={handleCloseClick}
    >
      <div className="pwa-installing-mask__modal">
        {isInstalled && (
          <div
            className="pwa-installing-mask__close"
            onClick={handleCloseClick}
          >
            <ShareAppBarCloseIcon color="#000" />
          </div>
        )}
        <div className="pwa-installing-mask__qi">
          <div className="pwa-installing-mask__qi-header">
            <img
              className="pwa-installing-mask__qi-logo"
              src={IconGooglePlay}
              alt=""
            />
            <h2 className="pwa-installing-mask__qi-title">
              {t("landing.quickInstallation")}
            </h2>
          </div>

          <div className="pwa-installing-mask__qi-badges">
            <span className="pwa-installing-mask__badge-light">
              {t("landing.timesFaster")}
            </span>
            <span className="pwa-installing-mask__badge-green">
              <IconVerified />
              {t("landing.activated")}
            </span>
          </div>

          {rounded < 100 && (
            <div className="pwa-installing-mask__qi-progress-row">
              <div className="pwa-installing-mask__progress-bar">
                <div
                  className="pwa-installing-mask__progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="pwa-installing-mask__progress-percent">
                {rounded}%
              </div>
            </div>
          )}

          {rounded >= 100 && (
            <button
              className="pwa-installing-mask__qi-play"
              type="button"
              onClick={handleOkClick}
            >
              {t("app.open")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PwaInstallingMask;
